import { useCallback, useEffect, useState } from 'react';
import {
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api, type PlanoDoPainel } from '@/lib/api';
import { planoPorId, type AjustesDePlano, type LimitesPlano, type Plano } from '@miolos/core';
import { cn } from '@/lib/utils';

/** Valor usado nos limites quando o plano não tem teto. */
const ILIMITADO = -1;

interface Rascunho {
  nome: string;
  resumo: string;
  publico: string;
  preco: string;
  precoDe: string;
  selo: string;
  destaque: boolean;
  recursos: string;
  miolos: string;
  cadernosPorMiolo: string;
  camadasPorMiolo: string;
  cadernosAvancados: boolean;
  usoComercial: boolean;
  suportePrioritario: boolean;
  atualizacoesVitalicias: boolean;
}

/** Centavos para o texto do campo, no formato brasileiro: 2700 → "27,00". */
function paraReais(centavos: number): string {
  return (centavos / 100).toFixed(2).replace('.', ',');
}

/** Texto do campo para centavos. Aceita "27", "27,50" e "1.299,90". */
function paraCentavos(texto: string): number | null {
  const limpo = texto
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');
  if (!limpo) return null;
  const valor = Number(limpo);
  if (!Number.isFinite(valor) || valor < 0) return null;
  return Math.round(valor * 100);
}

function paraInteiro(texto: string, padrao: number): number {
  const valor = Number(texto.trim());
  return Number.isInteger(valor) ? valor : padrao;
}

function rascunhoDoPlano(plano: Plano): Rascunho {
  return {
    nome: plano.nome,
    resumo: plano.resumo,
    publico: plano.publico,
    preco: paraReais(plano.precoCentavos),
    precoDe:
      typeof plano.precoDeCentavos === 'number' && plano.precoDeCentavos > 0
        ? paraReais(plano.precoDeCentavos)
        : '',
    selo: plano.selo ?? '',
    destaque: Boolean(plano.destaque),
    recursos: plano.recursos.join('\n'),
    miolos: String(plano.limites.miolos),
    cadernosPorMiolo: String(plano.limites.cadernosPorMiolo),
    camadasPorMiolo: String(plano.limites.camadasPorMiolo),
    cadernosAvancados: plano.limites.cadernosAvancados,
    usoComercial: plano.limites.usoComercial,
    suportePrioritario: plano.limites.suportePrioritario,
    atualizacoesVitalicias: plano.limites.atualizacoesVitalicias,
  };
}

/**
 * Compara o que está na tela com o catálogo padrão do core e devolve só as
 * diferenças. Enviar apenas o que mudou é o que permite ao sistema continuar
 * recebendo melhorias no catálogo sem congelar valores antigos.
 *
 * `null` significa “este plano está no padrão” — e o servidor limpa os ajustes.
 */
function montarAjustes(rascunho: Rascunho, base: Plano): AjustesDePlano | null {
  const diferencas: AjustesDePlano = {};

  const nome = rascunho.nome.trim();
  if (nome && nome !== base.nome) diferencas.nome = nome;

  const resumo = rascunho.resumo.trim();
  if (resumo && resumo !== base.resumo) diferencas.resumo = resumo;

  const publico = rascunho.publico.trim();
  if (publico && publico !== base.publico) diferencas.publico = publico;

  const preco = paraCentavos(rascunho.preco);
  if (preco !== null && preco !== base.precoCentavos) diferencas.precoCentavos = preco;

  const precoDe = rascunho.precoDe.trim() ? paraCentavos(rascunho.precoDe) : null;
  if (precoDe !== null && precoDe !== base.precoDeCentavos) {
    diferencas.precoDeCentavos = precoDe;
  } else if (precoDe === null && typeof base.precoDeCentavos === 'number') {
    diferencas.precoDeCentavos = null;
  }

  const selo = rascunho.selo.trim() || null;
  if (selo !== (base.selo ?? null)) diferencas.selo = selo;

  const destaque = rascunho.destaque;
  if (destaque !== Boolean(base.destaque)) diferencas.destaque = destaque;

  const recursos = rascunho.recursos
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean);
  if (recursos.join('|') !== base.recursos.join('|')) diferencas.recursos = recursos;

  const limites: Partial<LimitesPlano> = {};
  const miolos = paraInteiro(rascunho.miolos, base.limites.miolos);
  if (miolos !== base.limites.miolos) limites.miolos = miolos;
  const cadernos = paraInteiro(rascunho.cadernosPorMiolo, base.limites.cadernosPorMiolo);
  if (cadernos !== base.limites.cadernosPorMiolo) limites.cadernosPorMiolo = cadernos;
  const camadas = paraInteiro(rascunho.camadasPorMiolo, base.limites.camadasPorMiolo);
  if (camadas !== base.limites.camadasPorMiolo) limites.camadasPorMiolo = camadas;
  if (rascunho.cadernosAvancados !== base.limites.cadernosAvancados) {
    limites.cadernosAvancados = rascunho.cadernosAvancados;
  }
  if (rascunho.usoComercial !== base.limites.usoComercial) limites.usoComercial = rascunho.usoComercial;
  if (rascunho.suportePrioritario !== base.limites.suportePrioritario) {
    limites.suportePrioritario = rascunho.suportePrioritario;
  }
  if (rascunho.atualizacoesVitalicias !== base.limites.atualizacoesVitalicias) {
    limites.atualizacoesVitalicias = rascunho.atualizacoesVitalicias;
  }
  if (Object.keys(limites).length > 0) diferencas.limites = limites;

  return Object.keys(diferencas).length > 0 ? diferencas : null;
}

/** Quais campos estão diferentes do padrão, para o resumo do cartão. */
const ROTULO_CAMPO: Record<string, string> = {
  nome: 'nome',
  resumo: 'resumo',
  publico: 'para quem é',
  precoCentavos: 'preço',
  precoDeCentavos: 'preço riscado',
  destaque: 'destaque',
  selo: 'selo',
  recursos: 'lista de recursos',
  limites: 'limites',
};

interface Props {
  plano: PlanoDoPainel;
  aoSalvar: (id: string, dados: { oculto?: boolean; ajustes?: AjustesDePlano | null }) => Promise<void>;
  trabalhando: boolean;
}

function CartaoDePlano({ plano, aoSalvar, trabalhando }: Props) {
  const base = planoPorId(plano.id) ?? plano;
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoDoPlano(plano));

  useEffect(() => {
    setRascunho(rascunhoDoPlano(plano));
  }, [plano]);

  const alterar = <C extends keyof Rascunho>(campo: C, valor: Rascunho[C]) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const ajustes = montarAjustes(rascunho, base);
  const sujo = JSON.stringify(plano.ajustes ?? {}) !== JSON.stringify(ajustes ?? {});

  const camposAlterados = Object.entries(plano.ajustes ?? {})
    .filter(([, valor]) => valor !== undefined)
    .map(([chave]) => ROTULO_CAMPO[chave] ?? chave);

  const numero = (campo: keyof Rascunho, rotulo: string, ajuda?: string) => (
    <label className="space-y-1">
      <span className="block text-[11px] font-medium text-muted-foreground">{rotulo}</span>
      <Input
        type="number"
        min={ILIMITADO}
        value={String(rascunho[campo])}
        onChange={(evento) => alterar(campo, evento.target.value as Rascunho[typeof campo])}
        className="h-8 text-xs"
      />
      {ajuda ? <span className="block text-[10px] text-muted-foreground">{ajuda}</span> : null}
    </label>
  );

  const chave = (campo: keyof Rascunho, rotulo: string) => (
    <label className="flex items-center justify-between gap-2 rounded-md border border-border px-2.5 py-2">
      <span className="text-[11px] font-medium text-muted-foreground">{rotulo}</span>
      <Switch
        checked={Boolean(rascunho[campo])}
        onCheckedChange={(valor) => alterar(campo, valor as Rascunho[typeof campo])}
      />
    </label>
  );

  return (
    <article
      className={cn(
        'space-y-3 rounded-lg border bg-card p-4',
        plano.oculto ? 'border-dashed border-border opacity-90' : 'border-border',
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{plano.nome}</h3>
          <p className="text-[11px] text-muted-foreground">
            {plano.id} · {plano.cobranca === 'MENSAL' ? 'assinatura mensal' : plano.cobranca === 'UNICO' ? 'pagamento único' : 'por miolo'}
            {plano.diasDeCiclo ? ` de ${plano.diasDeCiclo} dias` : ''} · {plano.assinantes} licença(s)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {plano.oculto ? (
            <Badge tom="atencao">
              <EyeOff className="size-3" />
              fora do site
            </Badge>
          ) : (
            <Badge tom="sucesso">
              <Eye className="size-3" />
              no site
            </Badge>
          )}
          {camposAlterados.length > 0 ? (
            <Badge tom="principal" title={`Campos ajustados: ${camposAlterados.join(', ')}`}>
              <Sparkles className="size-3" />
              {camposAlterados.length} ajuste(s)
            </Badge>
          ) : (
            <Badge tom="neutro">padrão do catálogo</Badge>
          )}
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 sm:col-span-2">
          <span className="block text-[11px] font-medium text-muted-foreground">Nome do plano</span>
          <Input
            value={rascunho.nome}
            maxLength={70}
            onChange={(evento) => alterar('nome', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] font-medium text-muted-foreground">Preço (R$)</span>
          <Input
            value={rascunho.preco}
            onChange={(evento) => alterar('preco', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] font-medium text-muted-foreground">
            Preço riscado (R$) — vazio esconde o desconto
          </span>
          <Input
            value={rascunho.precoDe}
            placeholder="sem desconto"
            onChange={(evento) => alterar('precoDe', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="block text-[11px] font-medium text-muted-foreground">Resumo (uma linha)</span>
          <Input
            value={rascunho.resumo}
            maxLength={200}
            onChange={(evento) => alterar('resumo', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="block text-[11px] font-medium text-muted-foreground">Para quem é</span>
          <Input
            value={rascunho.publico}
            maxLength={200}
            onChange={(evento) => alterar('publico', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="block text-[11px] font-medium text-muted-foreground">
            O que está incluído (um item por linha)
          </span>
          <Textarea
            value={rascunho.recursos}
            rows={4}
            onChange={(evento) => alterar('recursos', evento.target.value)}
            className="text-xs"
          />
        </label>

        <label className="space-y-1">
          <span className="block text-[11px] font-medium text-muted-foreground">
            Selo (ex.: “mais escolhido”) — vazio esconde
          </span>
          <Input
            value={rascunho.selo}
            maxLength={40}
            placeholder="sem selo"
            onChange={(evento) => alterar('selo', evento.target.value)}
            className="h-8 text-xs"
          />
        </label>

        <label className="flex items-center justify-between gap-2 self-end rounded-md border border-border px-2.5 py-2">
          <span className="text-[11px] font-medium text-muted-foreground">Destacar no site</span>
          <Switch checked={rascunho.destaque} onCheckedChange={(valor) => alterar('destaque', valor)} />
        </label>
      </div>

      <div className="space-y-2">
        <Label className="text-[11px]">Limites do plano</Label>
        <div className="grid gap-2 sm:grid-cols-3">
          {numero('miolos', 'Miolos (= downloads)', '-1 = ilimitado')}
          {numero('cadernosPorMiolo', 'Cadernos por miolo')}
          {numero('camadasPorMiolo', 'Camadas de imagem por miolo')}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {chave('cadernosAvancados', 'Cadernos avançados')}
          {chave('usoComercial', 'Licença comercial')}
          {chave('suportePrioritario', 'Suporte prioritário')}
          {chave('atualizacoesVitalicias', 'Atualizações vitalícias')}
        </div>
      </div>

      <footer className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Button
          tamanho="pequeno"
          onClick={() => aoSalvar(plano.id, { ajustes })}
          disabled={trabalhando}
        >
          {trabalhando ? <Loader2 className="animate-spin" /> : <Save />}
          Salvar alterações
        </Button>

        <Button
          tamanho="pequeno"
          variant="contorno"
          onClick={() => aoSalvar(plano.id, { ajustes: null })}
          disabled={trabalhando || !plano.ajustes}
          title="Devolve o plano aos valores do catálogo."
        >
          <RotateCcw />
          Restaurar padrão
        </Button>

        <Button
          tamanho="pequeno"
          variant="contorno"
          onClick={() => aoSalvar(plano.id, { oculto: !plano.oculto })}
          disabled={trabalhando}
          title={
            plano.oculto
              ? 'Volta a exibir o plano no site, permitindo novas compras.'
              : 'Tira o plano do site. Quem já tem licença continua com o acesso.'
          }
        >
          {plano.oculto ? <Eye /> : <EyeOff />}
          {plano.oculto ? 'Exibir no site' : 'Ocultar do site'}
        </Button>

        {sujo ? (
          <span className="ml-auto text-[11px] font-medium text-amber-600 dark:text-amber-400">
            alterações não salvas
          </span>
        ) : null}
      </footer>
    </article>
  );
}

/**
 * Planos do catálogo, editáveis pelo administrador.
 *
 * O catálogo base vive no core (nome, preço, limites sugeridos). Aqui o dono do
 * sistema ajusta tudo sem deploy: preço, textos, o que está incluído, limites de
 * uso e se o plano aparece no site. Só o que for diferente do padrão é gravado,
 * então o plano continua recebendo melhorias do catálogo quando está “no padrão”.
 */
export function PlanosDoPainel() {
  const [planos, setPlanos] = useState<PlanoDoPainel[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [trabalhando, setTrabalhando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await api.listarPlanosDoPainel();
      setPlanos(resposta.planos);
    } catch (falha) {
      setErro(
        falha instanceof Error
          ? falha.message
          : 'Não foi possível carregar os planos. Entre novamente com a conta de administrador.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvar = async (
    id: string,
    dados: { oculto?: boolean; ajustes?: AjustesDePlano | null },
  ) => {
    setTrabalhando(id);
    setErro(null);
    setAviso(null);
    try {
      await api.salvarPlano(id, dados);
      await carregar();
      setAviso(
        dados.ajustes === null
          ? 'Plano restaurado ao padrão do catálogo.'
          : dados.oculto !== undefined
            ? dados.oculto
              ? 'Plano oculto: não aparece mais no site.'
              : 'Plano de volta ao site.'
            : 'Plano atualizado — o site já está usando os novos valores.',
      );
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível salvar o plano.');
    } finally {
      setTrabalhando(null);
    }
  };

  const ocultos = planos.filter((plano) => plano.oculto).length;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="size-4" />
          Planos do site
        </h2>
        <Badge tom="neutro">{planos.length} plano(s)</Badge>
        {ocultos > 0 ? <Badge tom="atencao">{ocultos} fora do site</Badge> : null}
        <Button variant="contorno" tamanho="pequeno" className="ml-auto" onClick={carregar} disabled={carregando}>
          {carregando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Atualizar
        </Button>
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Edite preço, textos e limites e clique em <strong className="text-foreground">Salvar alterações</strong> — a
        página de planos passa a mostrar os novos valores na hora, sem publicar o site de novo.
        <strong className="text-foreground"> Ocultar do site</strong> tira o plano da vitrine e bloqueia novas compras;
        quem já tem licença continua com o acesso. <strong className="text-foreground">Restaurar padrão</strong>{' '}
        devolve o plano ao catálogo original.
      </p>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aviso ? (
        <p className="rounded-lg border border-success/40 bg-success/5 p-3 text-xs text-success">{aviso}</p>
      ) : null}

      {carregando && planos.length === 0 ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando planos...
        </p>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {planos.map((plano) => (
            <CartaoDePlano
              key={plano.id}
              plano={plano}
              aoSalvar={salvar}
              trabalhando={trabalhando === plano.id}
            />
          ))}
        </div>
      )}
    </section>
  );
}
